import mixpanel from 'mixpanel-browser';

import { BuildADashTrackingEvents } from '@revelio/core';

import { SelectionCategoryLabelOverrides } from '../../shared/selection-lists/selection-category-label-overrides';
import type { ReportPageConfig } from '../CustomDash/components/PageConfigProvider/types';
import type { IReportEntityMap } from '../reports/report-builder/02-entity-configuration/mw-reports/utils';
import { reportBuilderStore } from '../reports/report-builder/report-builder.repository';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../reports/utils/report-builder-paths';
import type { GlobalAssistantReportOffer } from './global-ai-assistant-api';
import {
  type GlobalAssistantReportParamSuggestion,
  type GlobalAssistantReportPrefillWarning,
  postGlobalAssistantReportParams,
} from './global-ai-assistant-api';
import {
  globalAssistantReportLaunchPendingStore,
  promotedLaunchNavigationState,
} from './global-ai-assistant-report-launch-pending';
import { countResolvedSuggestions } from './global-ai-assistant-report-param-mapping';
import { applyGlobalAssistantReportPrefill } from './global-ai-assistant-report-prefill';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';

type RoleTaxonomy = {
  rolesTax: string;
  skillsTax: number;
};

type ReportEntityStepNavigate = (
  to: string,
  options?: {
    replace?: boolean;
    state?: { skipGuard?: boolean; promotedLaunchSeq?: number };
  }
) => void;

type FetchReportOfferParamsResult =
  | {
      ok: true;
      suggestions: GlobalAssistantReportParamSuggestion[];
    }
  | {
      ok: false;
      errorMessage: string;
    };

const toPrefillErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'unknown';

const toTitleCaseFromSnake = (value: string): string =>
  value
    .split('_')
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatSelectionLevelForWarning = (levelId: string): string => {
  const overrideLabel = SelectionCategoryLabelOverrides[levelId]?.trim();
  if (overrideLabel) {
    const normalizedOverride = overrideLabel.toUpperCase();
    if (levelId.startsWith('role_')) {
      return `Role ${normalizedOverride}`;
    }
    if (levelId.startsWith('skill')) {
      return `Skill ${normalizedOverride}`;
    }
    return overrideLabel;
  }

  if (levelId.startsWith('role_')) {
    const roleSuffix = levelId.replace('role_', '').toUpperCase();
    return `Role ${roleSuffix}`;
  }

  if (levelId.startsWith('skill')) {
    const skillSuffix = levelId.replace('skill_', '').toUpperCase();
    return `Skill ${skillSuffix}`;
  }

  return toTitleCaseFromSnake(levelId);
};

const buildReportOfferPrefillErrorText = (
  reportName: string,
  errorMessage: string
): string =>
  `We couldn't extract filters for the ${reportName} report (${errorMessage}). You can still configure the report manually in the builder.`;

const buildReportOfferPrefillWarningText = (
  warning: GlobalAssistantReportPrefillWarning
): string => {
  if (warning.reason === 'company_not_in_list') {
    return `We found "${warning.selectionName}" for ${warning.parameterDisplayName}, but this report only supports a limited set of companies and "${warning.selectionName}" isn't one of them. Please select a supported company manually.`;
  }

  const foundLevel = formatSelectionLevelForWarning(warning.foundLevelId ?? '');
  const supportedLevels = (warning.supportedLevelIds ?? [])
    .map((levelId) => formatSelectionLevelForWarning(levelId))
    .join(', ');
  return `We found "${warning.selectionName}" for ${warning.parameterDisplayName}, but it is in ${foundLevel} and this report supports ${supportedLevels}. Please select this value manually.`;
};

const trackPromotedPrefillMetrics = (
  reportType: string,
  suggestions: GlobalAssistantReportParamSuggestion[]
): void => {
  mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_PREFILL, {
    global_assistant_report_type: reportType,
    global_assistant_report_prefill_extracted: suggestions.length,
    global_assistant_report_prefill_resolved:
      countResolvedSuggestions(suggestions),
    global_assistant_report_prefill_applied: 0,
    global_assistant_report_prefill_skipped:
      suggestions.length - countResolvedSuggestions(suggestions),
  });
};

const trackPromotedPrefillEmpty = (reportType: string): void => {
  mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_PREFILL, {
    global_assistant_report_type: reportType,
    global_assistant_report_prefill_extracted: 0,
    global_assistant_report_prefill_resolved: 0,
    global_assistant_report_prefill_applied: 0,
    global_assistant_report_prefill_skipped: 0,
    global_assistant_report_prefill_empty: true,
  });
};

const trackLegacyPrefillMetrics = (
  reportType: string,
  prefillResult: {
    extractedCount: number;
    resolvedCount: number;
    appliedCount: number;
    skippedCount: number;
  }
): void => {
  mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_PREFILL, {
    global_assistant_report_type: reportType,
    global_assistant_report_prefill_extracted: prefillResult.extractedCount,
    global_assistant_report_prefill_resolved: prefillResult.resolvedCount,
    global_assistant_report_prefill_applied: prefillResult.appliedCount,
    global_assistant_report_prefill_skipped: prefillResult.skippedCount,
  });
};

const trackPrefillFailure = (
  reportType: string,
  errorMessage: string
): void => {
  mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_PREFILL, {
    global_assistant_report_type: reportType,
    global_assistant_report_prefill_extracted: 0,
    global_assistant_report_prefill_resolved: 0,
    global_assistant_report_prefill_applied: 0,
    global_assistant_report_prefill_skipped: 0,
    global_assistant_report_prefill_error: errorMessage,
  });
};

const fetchReportOfferParamSuggestions = async (params: {
  promptForParams: string;
  reportType: string;
  roleTaxonomy: RoleTaxonomy;
}): Promise<FetchReportOfferParamsResult> => {
  try {
    // No `geoTax` here, deliberately: prebuilt reports are MSA-anchored and
    // the prefill guardrail rejects any level other than msa/metro_area/city,
    // so an administrative taxonomy would resolve a county, collapse to a
    // state and be DROPPED — leaving the report with no geography at all.
    // Geography intentionally resolves under METRO for reports; the dash-meta
    // side was reverted to match (see 6e5600bdfd). Do not "fix" this.
    const suggestions = await postGlobalAssistantReportParams({
      prompt: params.promptForParams,
      reportType: params.reportType,
      rolesTax: params.roleTaxonomy.rolesTax,
      skillsTax: params.roleTaxonomy.skillsTax,
    });
    return { ok: true, suggestions };
  } catch (prefillError) {
    return {
      ok: false,
      errorMessage: toPrefillErrorMessage(prefillError),
    };
  }
};

const navigateToReportEntityStep = (
  navigate: ReportEntityStepNavigate,
  skipGuard: boolean
): void => {
  const entityPath = getReportBuilderPath(ReportBuilderStep.ENTITY);
  const navigationOptions = skipGuard
    ? { replace: true, state: { skipGuard: true } }
    : { replace: true };

  navigate(entityPath, navigationOptions);
};

export const launchReportFromOffer = async (params: {
  offer: GlobalAssistantReportOffer;
  promptForParams: string;
  roleTaxonomy: RoleTaxonomy;
  promotedTemplate: ReportPageConfig | null;
  reportEntityMap: IReportEntityMap;
  pathname: string;
  navigate: ReportEntityStepNavigate;
  skipGuard?: boolean;
  onPrefillError: (text: string) => void;
  onPrefillWarning?: (text: string) => void;
  onPrefillSuggestions?: (
    suggestions: GlobalAssistantReportParamSuggestion[]
  ) => void;
}): Promise<void> => {
  const {
    offer,
    promptForParams,
    roleTaxonomy,
    promotedTemplate,
    reportEntityMap,
  } = params;

  if (promotedTemplate) {
    let paramSuggestions: GlobalAssistantReportParamSuggestion[] = [];
    if (promptForParams) {
      const paramResult = await fetchReportOfferParamSuggestions({
        promptForParams,
        reportType: offer.reportType,
        roleTaxonomy,
      });
      if (paramResult.ok) {
        paramSuggestions = paramResult.suggestions;
        params.onPrefillSuggestions?.(paramResult.suggestions);
        if (paramResult.suggestions.length > 0) {
          trackPromotedPrefillMetrics(
            offer.reportType,
            paramResult.suggestions
          );
        } else {
          trackPromotedPrefillEmpty(offer.reportType);
        }
      } else {
        trackPrefillFailure(offer.reportType, paramResult.errorMessage);
        params.onPrefillError(
          buildReportOfferPrefillErrorText(
            offer.reportName,
            paramResult.errorMessage
          )
        );
      }
    }

    // Published with its final suggestions, together with the navigation
    // that delivers it: the report builder takes the template from there.
    reportBuilderStore.actions.setReports([]);
    globalAssistantReportLaunchPendingStore.actions.setPromotedLaunch({
      promotedTemplateId: promotedTemplate.id,
      reportType: offer.reportType,
      paramSuggestions,
    });
    params.navigate(getReportBuilderPath(ReportBuilderStep.ENTITY), {
      replace: true,
      state: {
        ...(params.skipGuard ? { skipGuard: true } : {}),
        ...promotedLaunchNavigationState(
          globalAssistantReportLaunchPendingStore.getState().launchSeq
        ),
      },
    });
    return;
  }

  globalAssistantReportLaunchPendingStore
    .getState()
    .actions.clearPromotedLaunch();

  const entityPath = getReportBuilderPath(ReportBuilderStep.ENTITY);
  if (globalAssistantPathsMatch(params.pathname, entityPath)) {
    globalAssistantReportLaunchPendingStore
      .getState()
      .actions.bumpMwReportLaunchSeq();
  }

  reportBuilderStore.actions.setReports([
    {
      id: 0,
      reportType: offer.reportType,
      selections: {},
    },
  ]);

  if (promptForParams) {
    const paramResult = await fetchReportOfferParamSuggestions({
      promptForParams,
      reportType: offer.reportType,
      roleTaxonomy,
    });
    if (paramResult.ok) {
      globalAssistantReportLaunchPendingStore.actions.setMwReportPrefill({
        reportType: offer.reportType,
        paramSuggestions: paramResult.suggestions,
      });
      params.onPrefillSuggestions?.(paramResult.suggestions);
      const prefillResult = await applyGlobalAssistantReportPrefill({
        reportId: 0,
        reportType: offer.reportType,
        suggestions: paramResult.suggestions,
        reportEntityMap,
      });
      trackLegacyPrefillMetrics(offer.reportType, prefillResult);
      prefillResult.warnings?.forEach((warning) => {
        params.onPrefillWarning?.(buildReportOfferPrefillWarningText(warning));
      });
    } else {
      trackPrefillFailure(offer.reportType, paramResult.errorMessage);
      params.onPrefillError(
        buildReportOfferPrefillErrorText(
          offer.reportName,
          paramResult.errorMessage
        )
      );
    }
  }

  navigateToReportEntityStep(params.navigate, params.skipGuard ?? false);
};
